import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDashboard } from '@/api/use-dashboard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Flame,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  BookOpen,
  CheckCircle2,
  Copy,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

/* ---------- ProgressRing ---------- */

function ProgressRing({ percent, size = 120 }: { percent: number; size?: number }) {
  const strokeWidth = 10;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;
  const center = size / 2;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke="#10b981"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
          className="transition-all duration-700"
        />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-emerald-600"
          fontSize="28"
          fontWeight="700"
        >
          {percent}%
        </text>
      </svg>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Daily Progress
      </span>
    </div>
  );
}

/* ---------- Skeleton ---------- */

function SkeletonCard() {
  return (
    <Card className="rounded-xl shadow-sm">
      <CardContent className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Review Summary helpers ---------- */

interface ReviewGroup {
  category: string;
  count: number;
  status: 'HIGH PRIORITY' | 'NEEDS FOCUS' | 'STABLE';
  trend: 'up' | 'down' | 'flat';
}

function getStatusBadgeClasses(status: ReviewGroup['status']) {
  switch (status) {
    case 'HIGH PRIORITY':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'NEEDS FOCUS':
      return 'bg-pink-100 text-pink-700 border-pink-200';
    case 'STABLE':
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

function TrendIcon({ trend }: { trend: ReviewGroup['trend'] }) {
  switch (trend) {
    case 'up':
      return <TrendingUp className="w-4 h-4 text-emerald-500" />; // HIGH PRIORITY
    case 'down':
      return <TrendingDown className="w-4 h-4 text-red-500" />; // NEEDS FOCUS
    case 'flat':
      return <Minus className="w-4 h-4 text-gray-400" />; // STABLE
  }
}

/* ---------- Main Component ---------- */

export default function Dashboard() {
  const { data, isLoading } = useDashboard();
  const { toast } = useToast();
  const { user } = useAuth();

  const overview = data?.overview;

  // XP calculation
  const xpEarned = data?.daily_xp?.earned ?? 0;
  const xpGoal = data?.daily_xp?.goal ?? 2000;

  // Training sidebar
  const trainingComplete = overview?.training_complete ?? false;
  const trainedCardCount = (overview as any)?.trained_card_count ?? 0;
  const dailyCardCount = (overview as any)?.daily_card_count ?? 5;
  const reviewComplete = overview?.review_complete ?? false;
  const dueCount = overview?.due_items_count ?? 0;
  const reviewedCount = (overview as any)?.reviewed_count ?? 0;
  const totalReviewable = reviewedCount + dueCount;

  // Daily progress — weighted average of training + review sub-progress
  const trainingProgress = dailyCardCount > 0
    ? Math.min(100, (trainedCardCount / dailyCardCount) * 100)
    : 0;
  const reviewProgress = totalReviewable > 0
    ? Math.min(100, (reviewedCount / totalReviewable) * 100)
    : (dueCount === 0 ? 100 : 0); // if nothing due, review is "done"
  // 50% weight each; if no reviews due, training is 100% weight
  const progress = dueCount === 0 && !overview?.training_complete
    ? trainingProgress
    : Math.round((trainingProgress + reviewProgress) / 2);

  // Group due_items by item_type for Review Summary
  const reviewGroups: ReviewGroup[] = useMemo(() => {
    const items = data?.due_items ?? [];
    const now = new Date();
    const grouped = items.reduce<
      Record<string, { count: number; hasOverdue: boolean }>
    >((acc, item) => {
      if (!acc[item.item_type]) {
        acc[item.item_type] = { count: 0, hasOverdue: false };
      }
      acc[item.item_type].count += 1;
      if (new Date(item.next_review) < now) {
        acc[item.item_type].hasOverdue = true;
      }
      return acc;
    }, {});

    return Object.entries(grouped).map(([category, { count }]) => {
      let status: ReviewGroup['status'] = 'STABLE';
      if (count > 5) status = 'HIGH PRIORITY';
      else if (count > 2) status = 'NEEDS FOCUS';

      let trend: ReviewGroup['trend'] = 'flat';
      if (status === 'HIGH PRIORITY') trend = 'up';
      else if (status === 'NEEDS FOCUS') trend = 'down';

      return { category, count, status, trend };
    });
  }, [data?.due_items]);

  /* ---------- Loading ---------- */

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonCard />
          </div>
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  /* ---------- Render ---------- */

  return (
    <div className="space-y-8">
      {/* ===== Top Row: Hero + Sidebar ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* --- Hero Card --- */}
        <Card className="lg:col-span-2 rounded-xl shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-start justify-between">
              {/* Left content */}
              <div className="flex-1 space-y-4">
                {/* Active session indicator */}
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Active Session
                  </span>
                </div>

                {/* Greeting */}
                <div>
                  <h2 className="text-4xl font-bold tracking-tight">
                    Good morning{user?.display_name ? `, ${user.display_name.split(' ')[0]}` : ''}
                  </h2>
                  <p className="text-base text-slate-500 mt-1 line-clamp-2">
                    You're on a roll! Complete today's review to keep your streak
                    alive.
                  </p>
                </div>

                {/* Streak + XP badges */}
                <div className="flex items-center gap-3 pt-2">
                  {/* Streak badge */}
                  <div className="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-orange-950/30 px-3 py-2">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Current Streak
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Flame className="w-4 h-4 text-orange-500" />
                        <span className="text-lg font-bold tabular-nums text-orange-600 dark:text-orange-400">
                          {overview?.streak ?? 0} days
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Daily goal badge */}
                  <div className="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-indigo-950/30 px-3 py-2">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Daily Goal
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-lg font-bold tabular-nums text-indigo-600 dark:text-indigo-400">
                          {xpEarned.toLocaleString()}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          / {xpGoal.toLocaleString()} XP
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: progress ring */}
              <div className="hidden sm:flex ml-6">
                <ProgressRing percent={progress} size={120} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* --- Right Sidebar --- */}
        <div className="space-y-4">
          {/* Training card */}
          <Card className="rounded-xl shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-950/40">
                    <BookOpen className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <Badge
                    className={cn(
                      'text-[10px] uppercase tracking-wider',
                      trainingComplete
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                        : 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100',
                    )}
                  >
                    {trainingComplete ? 'Completed' : 'In Progress'}
                  </Badge>
                </div>
              </div>
              <h3 className="font-semibold text-sm">Training</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {trainedCardCount}/{dailyCardCount} cards completed today
              </p>
              <Progress
                value={dailyCardCount > 0 ? Math.min(100, (trainedCardCount / dailyCardCount) * 100) : 0}
                className="mt-3 h-2 bg-emerald-100 dark:bg-emerald-950/30 [&>div]:bg-emerald-500"
              />
            </CardContent>
          </Card>

          {/* Review card */}
          <Card className="rounded-xl shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 dark:bg-red-950/40">
                    <RefreshCw className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </div>
                  <Badge
                    className={cn(
                      'text-[10px] uppercase tracking-wider',
                      reviewComplete
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                        : 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
                    )}
                  >
                    {reviewComplete
                      ? 'Completed'
                      : `${Math.min(dueCount, totalReviewable)} Remaining`}
                  </Badge>
                </div>
              </div>
              <h3 className="font-semibold text-sm">Review</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {reviewedCount}/{totalReviewable} reviewed today
              </p>
              <Progress
                value={totalReviewable > 0 ? (reviewedCount / totalReviewable) * 100 : 0}
                className="mt-3 h-2 bg-emerald-100 dark:bg-emerald-950/30 [&>div]:bg-emerald-500"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== Recommended Scenes ===== */}
      {(data?.recommended_cards?.length ?? 0) === 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Recommended Scenes</h2>
          </div>
          <Card className="rounded-xl shadow-sm">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground text-sm">Add scenes and practice to get personalized recommendations.</p>
            </CardContent>
          </Card>
        </section>
      )}
      {(data?.recommended_cards?.length ?? 0) > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recommended Scenes</h2>
            <Link
              to="/scenes"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
            >
              View All Scenes
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(data?.recommended_cards ?? []).slice(0, 2).map((card, idx) => {
              const difficultyLabel = card.difficulty <= 3
                ? 'Beginner'
                : card.difficulty <= 6
                  ? 'Intermediate'
                  : 'Advanced';
              const difficultyClasses = card.difficulty <= 3
                ? 'bg-emerald-500/70 text-white'
                : card.difficulty <= 6
                  ? 'bg-gray-500/70 text-white'
                  : 'bg-amber-500/70 text-white';

              const getTagColor = (tag: string) => {
                const t = tag.toLowerCase();
                if (t.includes('life') || t.includes('daily')) return 'bg-slate-500/70 text-white';
                if (t.includes('social')) return 'bg-blue-500/70 text-white';
                if (t.includes('professional') || t.includes('work') || t.includes('business')) return 'bg-rose-500/70 text-white';
                return 'bg-slate-500/70 text-white';
              };

              return (
                <Card key={card.card_id} className="overflow-hidden rounded-xl shadow-sm">
                  {/* Gradient placeholder image */}
                  <div
                    className={cn(
                      'relative h-40',
                      idx === 0
                        ? 'bg-gradient-to-r from-amber-700 to-amber-900'
                        : 'bg-gradient-to-r from-slate-600 to-slate-800',
                    )}
                  >
                    <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
                      {(card.tag_names ?? []).map((tag) => (
                        <Badge
                          key={tag}
                          className={cn(
                            'border-0 text-[10px] uppercase tracking-wider backdrop-blur-sm',
                            getTagColor(tag),
                          )}
                        >
                          {tag}
                        </Badge>
                      ))}
                      <Badge className={cn(
                        'border-0 text-[10px] uppercase tracking-wider backdrop-blur-sm',
                        difficultyClasses,
                      )}>
                        {difficultyLabel}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{card.card_name}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {card.group_name} &rsaquo; {card.scene_name}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold tabular-nums text-indigo-600 dark:text-indigo-400">
                          {card.avg_score != null
                            ? (card.avg_score * 100).toFixed(0)
                            : '--'}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Mastery
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        const cardRef = card.card_sid ?? card.card_id;
                        navigator.clipboard.writeText(`/cadence train ${cardRef}`);
                        toast({ title: 'Command Copied', description: `Paste in your AI chat to practice ${card.card_name}.` });
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-sm text-slate-600 font-medium transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      Copy Training Command
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ===== Review Summary ===== */}
      {reviewGroups.length === 0 && (
        <Card className="rounded-xl shadow-sm">
          <CardContent className="py-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            <h3 className="font-semibold">All caught up!</h3>
            <p className="text-sm text-muted-foreground mt-1">No items due for review right now.</p>
          </CardContent>
        </Card>
      )}
      {reviewGroups.length > 0 && (
        <Card className="rounded-xl shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Review Summary</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Prioritizing spaced-repetition for today.
                </p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('/cadence review');
                  toast({ title: 'Command Copied', description: 'Paste /cadence review in your AI chat to start reviewing.' });
                }}
                className="inline-flex items-center bg-rose-500 hover:bg-rose-600 text-white rounded-lg px-5 py-2 text-sm font-medium transition-colors active:scale-[0.97]"
              >
                Start Review
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Table header */}
            <div className="grid grid-cols-4 gap-4 px-2 pb-2 border-b text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Category</span>
              <span className="text-center">Count</span>
              <span className="text-center">Status</span>
              <span className="text-center">Trend</span>
            </div>

            {/* Table rows */}
            <div className="divide-y">
              {reviewGroups.map((group) => (
                <div
                  key={group.category}
                  className="grid grid-cols-4 gap-4 px-2 py-3 items-center"
                >
                  <span className="text-sm font-semibold capitalize">
                    {group.category}s
                  </span>
                  <span className="text-sm text-center font-semibold">
                    {group.count}
                  </span>
                  <div className="flex justify-center">
                    <Badge
                      className={cn(
                        'text-[10px] uppercase tracking-wider',
                        getStatusBadgeClasses(group.status),
                      )}
                    >
                      {group.status}
                    </Badge>
                  </div>
                  <div className="flex justify-center">
                    <TrendIcon trend={group.trend} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
